import { createTheme, alpha } from '@mui/material/styles';

const accent = '#E50914';
const accentSoft = '#FF4D5E';

const sharedTypography = {
  fontFamily:
    '"Inter", "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  h1: { fontWeight: 800, letterSpacing: '-0.02em' },
  h2: { fontWeight: 800, letterSpacing: '-0.02em' },
  h3: { fontWeight: 700, letterSpacing: '-0.01em' },
  h4: { fontWeight: 700, letterSpacing: '-0.01em' },
  h5: { fontWeight: 700 },
  h6: { fontWeight: 600 },
  button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
};

const sharedShape = { borderRadius: 12 };

const buildComponents = (mode) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollbarColor:
          mode === 'dark' ? '#2a2a36 #0a0a0f' : '#cbd5e1 #f1f5f9',
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
          width: 10,
          height: 10,
        },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          backgroundColor: mode === 'dark' ? '#2a2a36' : '#cbd5e1',
          borderRadius: 8,
        },
        '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
          backgroundColor: mode === 'dark' ? '#3a3a48' : '#94a3b8',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 999,
        paddingInline: 20,
        paddingBlock: 8,
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
      },
      containedPrimary: {
        background: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
        color: '#fff',
        '&:hover': {
          background: `linear-gradient(135deg, ${accentSoft} 0%, ${accent} 100%)`,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        backgroundImage: 'none',
        border:
          mode === 'dark'
            ? `1px solid ${alpha('#ffffff', 0.06)}`
            : `1px solid ${alpha('#000000', 0.06)}`,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backdropFilter: 'saturate(180%) blur(14px)',
        WebkitBackdropFilter: 'saturate(180%) blur(14px)',
        backgroundColor:
          mode === 'dark'
            ? alpha('#0a0a0f', 0.7)
            : alpha('#ffffff', 0.75),
        borderBottom:
          mode === 'dark'
            ? `1px solid ${alpha('#ffffff', 0.06)}`
            : `1px solid ${alpha('#000000', 0.06)}`,
      },
    },
  },
  MuiTextField: {
    defaultProps: { variant: 'outlined', size: 'small' },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        backgroundColor:
          mode === 'dark' ? alpha('#ffffff', 0.04) : alpha('#000000', 0.02),
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: mode === 'dark' ? '#1c1c26' : '#111',
        fontSize: 12,
        borderRadius: 8,
        paddingInline: 10,
        paddingBlock: 6,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: { textTransform: 'none', fontWeight: 600, minHeight: 44 },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 3,
        borderRadius: 2,
        background: `linear-gradient(90deg, ${accent}, ${accentSoft})`,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 8, fontWeight: 500 },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: { textDecoration: 'none', fontWeight: 500 },
    },
  },
});

export const buildTheme = (mode = 'dark') =>
  createTheme({
    palette: {
      mode,
      primary: { main: accent, light: accentSoft, dark: '#B0060F' },
      secondary: { main: '#00B7FF' },
      ...(mode === 'dark'
        ? {
            background: { default: '#0a0a0f', paper: '#14141c' },
            text: { primary: '#F5F5F7', secondary: '#A1A1AA' },
            divider: alpha('#ffffff', 0.08),
          }
        : {
            background: { default: '#FAFAFB', paper: '#FFFFFF' },
            text: { primary: '#0a0a0f', secondary: '#52525B' },
            divider: alpha('#000000', 0.08),
          }),
    },
    shape: sharedShape,
    typography: sharedTypography,
    components: buildComponents(mode),
  });

export const ACCENT = accent;
export const ACCENT_SOFT = accentSoft;
